<template>
	<faux-modal class="social-login">
		<template v-if="!isWithinFrame">
			<c-auth-page-header
				v-if="pageTitle && !invitation.showErrorMessage"
				:title="pageTitle"
				:back-button="$route.meta.showBackButton"
			/>

			<div v-if="showInviteMessage" class="sz-2 text-center bg-harriet p-3 mb-2">
				<p v-if="invitation.Organization">You're invited to join {{ invitation.Organization }}.</p>
				<p v-else-if="apiConnection.integrationName">Connect your {{ integrationName }} account to Cognito Forms.</p>
			</div>
			<div v-if="$route.query.source == 'square'" class="sz-2 text-center bg-harriet p-3 mb-2">
				<p>Connect your Square account to Cognito Forms.</p>
			</div>
			<div v-if="showPlanRestrictionMessage" class="sz-2 text-center burnt-jaywalk bg-peach p-3 mb-2">
				<p>Please upgrade to a Pro plan to enable this feature.</p>
			</div>
		</template>

		<auth-within-frame
			v-if="isWithinFrame"
			:page-source="pageSource"
		>
			<template #button-text>
				<span v-if="pageSource === 'login'">Log into</span>
				<span v-else-if="pageSource === 'signup'">Sign Up</span> Cognito Forms
			</template>
			<template #description>
				<p class="text-center">
					Please <span v-if="pageSource === 'login'">login</span>
					<span v-else-if="pageSource === 'signup'">sign up</span> using the other window.
				</p>
			</template>
		</auth-within-frame>

		<transition v-else name="fade" mode="out-in">
			<router-view
				:prefilled-email="capturedEmail"
				:invitation="invitation"
				:api-connection="apiConnection"
				:signup-message="signUpMessage"
				@emailCaptured="setEmailAddress"
				@oAuthIntegrationCaptured="setIntegration"
				@oAuthIntegrationRestricted="handleRestriction"
				@title-changed="handleTitleChange"
			>
				<template v-if="botProtection.challenge" #captcha>
					<c-captcha ref="captcha" :bot-challenge="botProtection.challenge" @validated="captchaValidated" />
				</template>
			</router-view>
		</transition>

		<div v-if="showCognitoDisclaimers" class="social-login__tos text-center mt sz-2" @thirdPartyOAuth="toggleCognitoDisclaimers">
			<p><a href="/legal/terms" target="_blank" title="Agree to Terms of Service">Terms of Service</a> | <a href="/legal/privacy" target="_blank" title="Agree to Privacy Policy">Privacy Policy</a></p>
			<p>Copyright &copy; {{ new Date().getFullYear() }} Cognito LLC, All rights reserved. <br>Part of the <strong>Cognito Apps<sup>SM</sup></strong> family of products.</p>
		</div>

		<vue-progress-bar></vue-progress-bar>
		<c-app-insights ref="ai" />
	</faux-modal>
</template>

<script>
	import Vue from 'vue';
	import { enableBotProtection, interceptResponses } from '@cognitoforms/api/services/service-request';
	import { validateInviteCode } from '@cognitoforms/api/services/authentication';
	import CAuthPageHeader from 'src/components/auth/AuthPageHeader.vue';
	import CCaptcha from 'src/components/form-controls/Captcha.vue';
	import VueProgressBar from 'vue-progressbar';
	import { isWithinFrame } from '@cognitoforms/utils/utilities';
	import AuthWithinFrame from 'src/components/auth/AuthWithinFrame.vue';
	import { getMinimumPlan } from '@cognitoforms/api/services/templates-service';
	import { UserType } from '@cognitoforms/api/services/bot-protection';
	import FauxModal from '@cognitoforms/shared-components/src/layouts/FauxModal.vue';
	import CAppInsights from '@cognitoforms/shared-components/src/components/embeds/AppInsights';
	import { authStore } from 'src/stores/auth-store';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';

	Vue.use(VueProgressBar, {
		color: '#77e6e0', // thetis
		failedColor: '#f9a241', // blossom
		thickness: '3px'
	});

	export default {
		components: {
			CAuthPageHeader,
			CCaptcha,
			AuthWithinFrame,
			FauxModal,
			CAppInsights
		},
		async beforeRouteEnter(to, from, next) {
			if (CognitoConfiguration.NewLoginEnabled) {
				document.documentElement.setAttribute('data-auth-style', 'app');
				await import('@cognitoforms/styles/app/common/common.scss');
			}
			else {
				await Promise.all(
					[
						document.documentElement.setAttribute('data-auth-style', 'web'),
						import('@cognitoforms/styles/website/common/common.scss'),
						import('@cognitoforms/styles/website/common/utility.scss')
					]
				);
			}

			next();
		},
		beforeRouteUpdate(to, from, next) {
			// automatically pass query string parameters between auth routes
			if (!Object.keys(from.query).every(p => to.query[p] === from.query[p]))
				next({ ...to, query: Object.assign({}, from.query, to.query) });
			else {
				next();
			}
		},
		inheritAttrs: false,
		data() {
			return {
				botProtection: {},
				capturedEmail: null,
				inviteCode: null,
				invitation: {
					showInviteMessage: false,
					showErrorMessage: false
				},
				verificationCode: null,
				showCognitoDisclaimers: false,
				apiConnection: {
					userCode: this.$route.query.user,
					integrationName: this.$route.query.source || null,
					showInviteMessage: false
				},
				backButton: false,
				signUpMessage: null,
				title: null,
				showPlanRestrictionMessage: false,
				authStore,
				CognitoConfiguration
			};
		},
		head: {
			htmlAttrs: {
				class: CognitoConfiguration.NewLoginEnabled ? ['app not-iframe-page'] : ['website']
			}
		},
		computed: {
			pageTitle() {
				if (this.$route.name === 'api-connection')
					return this.apiConnection.integrationName + ' Authorization';
				if (this.showMfaMessage)
					return 'Two-Factor Authentication';
				if (this.showDeviceVerificationMessage)
					return 'New Device Detected';
				if (this.title || CognitoConfiguration.NewLoginEnabled)
					return this.title;
				return this.$route.meta.title;
			},
			pageSource() {
				return window.location.href.includes('login') ? 'login' : (window.location.href.includes('signup') || this.$route.query.templateName ? 'signup' : '');
			},
			showInviteMessage() {
				return !this.showMfaMessage && (this.apiConnection.showInviteMessage || this.invitation.showInviteMessage);
			},
			showMfaMessage() {
				return !!this.authStore.mfa.multifactorToken;
			},
			showDeviceVerificationMessage() {
				return !!this.authStore.deviceVerification.token;
			},
			isWithinFrame: isWithinFrame
		},
		async created() {
			interceptResponses(async res => {
				if (res.status === 400 && (await res.text()) === 'captcha')
					this.ensureCaptcha();
			});
			this.initAuthStore();
			this.inviteCode = this.$route.query.inviteCode;
			this.verificationCode = this.$route.query.verificationCode;
			if (this.inviteCode)
				this.invitationInformation();

			/*
				If the user comes from the Templates page ("Use This Template" button), we need to pass the selectedTemplateURL for the redirect
				to the builder after the user logs in/signs up
			*/
			if (['templates', 'shared-template'].includes(this.$route.query.source)) {
				const orgCode = this.$route.query.details.split('-')[0];
				const formId = this.$route.query.details.split('-')[1];
				getMinimumPlan(orgCode, formId).then(response => {
					if (response && response.Name !== 'Individual') {
						this.signUpMessage = 'We\'re signing you up for a Free 14-day ' + response.DisplayName + ' trial so you can use all of the features required by this template. No credit card required.';
					}
				});
			}

			if (this.$route.query.source === 'square') {
				this.signUpMessage = 'We\'re signing you up for a Free 14-day Team trial so you can use all of the features required by this template. No credit card required.';
			}

			this.botProtection = await enableBotProtection();
			this.botProtection.whenActivityDetected(bp => {
				if (bp.userType === UserType.Bot)
					this.ensureCaptcha();
			});
		},
		methods: {
			handleRestriction(e) {
				this.showPlanRestrictionMessage = e.value;
			},
			initAuthStore() {
				this.authStore.mfa.multifactorToken = this.$route.query.mfaToken;
				this.authStore.mfa.locked = this.$route.query.mfaLocked === 'true';
				this.authStore.mfa.encryptedAuthMethod = this.$route.query.encryptedAuthMethod;
				this.authStore.mfa.backupEmail = this.$route.query.mfaBackupEmail;
				this.authStore.deviceVerification.token = null;
			},
			toggleCognitoDisclaimers() {
				this.showCognitoDisclaimers = true;
			},
			captchaValidated(response) {
				this.botProtection.reportHuman(response);
			},
			async ensureCaptcha() {
				this.$refs.captcha && this.$refs.captcha.validateUser(true);
			},
			setEmailAddress(email) {
				this.capturedEmail = email;
			},
			setIntegration(integration) {
				this.apiConnection.integrationName = integration;
			},
			errorLoading() {
				this.invitation.showErrorMessage = true;
			},
			handleTitleChange(title) {
				this.title = title;
			},
			invitationInformation() {
				validateInviteCode(this.inviteCode)
					.then(async res => {
						this.invitation = res;
						this.invitation.inviteCode = this.inviteCode;
						this.invitation.showInviteMessage = true;
						this.invitation.verificationCode = this.verificationCode;

						/* If not logged in and they do not have an account, direct to signup
							OR If the user is currently logged into an account that is already a user of the org, show the sign up page if the invited email doesn't have an account.
						*/
						if ((!res.CurrentUserEmail && !res.AccountExists) || (res.CurrentUserIsMember && !res.AccountExists))
							return this.$router.push({ path: 'signup', query: { inviteCode: this.inviteCode, verificationCode: this.verificationCode, requiredAuthMethod: res.RequiredAuthMethod } });

						/* If not logged in, direct them to log in
							OR If the user is currently logged into an account that is already a user of the org, show the login page if the invited email has an account.
							OR If the user is currently logged in using an auth method other than the one required by the org
						*/
						if (!res.CurrentUserEmail || (res.CurrentUserIsMember && res.AccountExists) || (res.RequiredAuthMethod && res.RequiredAuthMethod !== res.AuthMethod))
							return this.$router.push({ path: 'login', query: { inviteCode: this.inviteCode, verificationCode: this.verificationCode, requiredAuthMethod: res.RequiredAuthMethod } });
					})
					.catch(e => {
						console.warn(`Error validating invite code: ${e}`);
						this.errorLoading();
					});
			}
		}
	};
</script>

<style lang="scss">
[data-auth-style='web'] .social-login {

	.faux-modal__body {
		@include contain-until($b5);
	}

	.faux-modal__wrapper {
		max-width: 320px;
	}

	a {
		color: $midnight;

		&:hover,
		&:focus,
		&:active {
			color: $poseidon;
		}
	}

	&__button {
		min-height: 46px;
		background: transparent;
		border: 1px solid $lange;
		box-shadow: 3px 3px 0 $mapplethorpe;
		padding: .6em 1em;
		color: $ansel;
		font-family: $display-type;
		text-align: left;
	}

	.facebook {
		width: 20px;
		margin: 2px;

		& > svg [fill] {
			fill: #1877f2; // FB brand blue
		}
	}

	&__guts > *:not(:last-child),
	&__form > * {
		margin-bottom: math.div($gutter, 2);

		.el-input__inner {
			border: 1px solid $lange;

			&:focus {
				border: 1px solid $casablanca;
			}

			& [data-whatinput='keyboard'] :focus:not(body) {
				box-shadow: none;
			}

			// border-style: solid;
		}
	}

	.strike {
		display: block;
		overflow: hidden;
		text-align: center;
		white-space: nowrap;

		& > span {
			display: inline-block;
			position: relative;
			color: $hitchcock;
			font-weight: 800;
			text-transform: uppercase;

			&::before,
			&::after {
				display: block;
				position: absolute;
				top: calc(50% - 1.5px);
				width: 9999px;
				height: 3px;
				background: $brady;
				content: '';
			}

			&::before {
				right: 100%;
				margin-right: 15px;
			}

			&::after {
				left: 100%;
				margin-left: 15px;
			}
		}
	}

	.el-input input,
	.button--primary,
	.button--secondary,
	&__button {
		display: block;
		width: 100%;
		border-radius: 0;
		-webkit-appearance: none;
	}

	.el-input__inner {
		padding: .6em;
		font-size: inherit;
	}

	h1 {
		margin-bottom: 16px;
	}
}
</style>